import BannerComponent from "slotworks/components/banner/banner-component";
import BetBarComponent from "slotworks/components/betbar/bet-bar-component";
import BetMenuComponent from "slotworks/components/betbar/bet-menu-component";
import BigWinComponent from "slotworks/components/bigwin/big-win-component";
import CelebrateWinComponent from "slotworks/components/celebrate-win/celebrate-win-component";
import HeaderComponent from "slotworks/components/header/header-component";
import FooterComponent from "slotworks/components/info/footer-component";
import LinesComponent from "slotworks/components/lines/lines-component";
import MatrixComponent from "slotworks/components/matrix/matrix-component";
import AbstractReelTransition from "slotworks/components/matrix/reel/transition-behaviours/abstract-reel-transition";
import AnimateReelTransition
    from "slotworks/components/matrix/reel/transition-behaviours/animate/animate-reel-transition";
import ReelSpinner from "slotworks/components/matrix/reel/transition-behaviours/spin/reel-spinner";
import SpinReelTransition from "slotworks/components/matrix/reel/transition-behaviours/spin/spin-reel-transition";
import AnimateOverSymbolBehaviour from "slotworks/components/matrix/symbol/behaviours/animate-over-symbol-behaviour";
import AnimateSymbolBehaviour from "slotworks/components/matrix/symbol/behaviours/animate-symbol-behaviour";
import AnticipateAnimateOverSymbolBehaviour
    from "slotworks/components/matrix/symbol/behaviours/anticipate-animate-over-symbol-behaviour";
import AnticipateAnimateSymbolBehaviour
    from "slotworks/components/matrix/symbol/behaviours/anticipate-animate-symbol-behaviour";
import BlinkSymbolBehaviour from "slotworks/components/matrix/symbol/behaviours/blink-symbol-behaviour";
import DebugSymbolBehaviour from "slotworks/components/matrix/symbol/behaviours/debug-symbol-behaviour";
import DimSymbolBehaviour from "slotworks/components/matrix/symbol/behaviours/dim-symbol-behaviour";
import ExpandAnimateSymbolBehaviour
    from "slotworks/components/matrix/symbol/behaviours/expand-animate-symbol-behaviour";
import LandAnimateOverSymbolBehaviour
    from "slotworks/components/matrix/symbol/behaviours/land-animate-over-symbol-behaviour";
import LandAnimateSymbolBehaviour from "slotworks/components/matrix/symbol/behaviours/land-animate-symbol-behaviour";
import LandPulseSymbolBehaviour from "slotworks/components/matrix/symbol/behaviours/land-pulse-symbol-behaviour";
import StickSymbolBehaviour from "slotworks/components/matrix/symbol/behaviours/stick-symbol-behaviour";
import StopBounceSymbolBehaviour from "slotworks/components/matrix/symbol/behaviours/stop-bounce-symbol-behaviour";
import PaytableComponent from "slotworks/components/paytable/paytable-component";
import PreloaderComponent from "slotworks/components/preloader/preloader-component";
import PromoBetBarComponent from "slotworks/components/promo-betbar/promo-bet-bar-component";
import PromptComponent from "slotworks/components/prompt/prompt-component";
import {ComponentConfigSchema} from "slotworks/config/component-config-schema";
import Layers from "slotworks/graphics/layers/layers";
import ParticleManager from "slotworks/graphics/particles/particle-manager";
import SlotDefinition from "slotworks/model/slot-definition";
import {BigWinConfig} from "../config/component-config-schema";
import {CountMode} from "./bigwin/big-win-component";
import {AnimateIdleSymbolBehavior} from "./matrix/symbol/behaviours/animate-idle-symbol-behavior";
import HelpPopup from "./promo-popups/help-popup";
import IntroPopup from "./promo-popups/intro-popup";
import SummaryPopup from "./promo-popups/summary-popup";
import DisplaySymbolBehaviour from "slotworks/components/matrix/symbol/behaviours/display-symbol-behaviour";

export default class Components {

    public static config: ComponentConfigSchema;

    public static preloader: PreloaderComponent;
    public static matrix: MatrixComponent;
    public static bigWin: BigWinComponent;
    public static lines: LinesComponent;
    public static celebrateWin: CelebrateWinComponent;
    public static betBar: BetBarComponent;
    public static promoBetBar: PromoBetBarComponent;
    public static promoPopups: Map<string, IntroPopup | SummaryPopup | HelpPopup>;
    public static prompt: PromptComponent;
    public static paytable: PaytableComponent;
    public static betMenu: BetMenuComponent;
    public static header: HeaderComponent;
    public static footer: FooterComponent;
    public static banner: BannerComponent;

    /**
     * Sets up components after just 1 stage has loaded (preloader)
     */
    public static preinit() {
        this.createPreloader();
        this.addParticles();
        this.createPrompts();
    }

    /**
     * Initialise basic standard components, using the component config to set them up.
     * Might split this class up later to have a factory for each component
     */
    public static init() {
        this.destroyPreInit();

        this.createMatrix();
        this.createBigWin();
        this.createLines();
        this.createCelebrateWin();
        this.createPrompts();
        this.createPaytable();
        this.createBetMenu();
        this.createBetBar();
        this.createHeader();
        this.createFooter();
        this.createBanner();
    }

    private static destroyPreInit() {
        Components.preloader.destroy();
    }

    private static addParticles() {
        Object.keys(Components.config.particles).forEach((key: string) => {
            ParticleManager.addConfig(key, Components.config.particles[key]);
        });
    }

    private static createPreloader() {
        this.preloader = new PreloaderComponent();
    }

    private static createMatrix() {
        const config = Components.config.matrix;

        if (config) {

            // Create matrix
            this.matrix = new MatrixComponent(SlotDefinition.matrixGrid, config.defaultStops, SlotDefinition.reelsets.get(config.defaultReelset));

            // Add transition (if configured)
            if (config.transition) {
                let transition: AbstractReelTransition;

                const tConfig = config.transition.options;

                if (config.transition.type === "spin") {
                    transition = new SpinReelTransition(tConfig.startGap, tConfig.stopGap, tConfig.quickStopGap, tConfig.allowPartialSkips);

                    if (config.transition.options.spinStages) {
                        ReelSpinner.spinStages = config.transition.options.spinStages;
                    }
                    if (config.transition.options.skipStages) {
                        ReelSpinner.skipStages = config.transition.options.skipStages;
                    }
                    if (config.transition.options.anticipationStages) {
                        ReelSpinner.anticipationStages = config.transition.options.anticipationStages;
                    }
                } else if (config.transition.type === "animate") {
                    transition = new AnimateReelTransition(tConfig.startGap, tConfig.stopGap, tConfig.quickStopGap);
                }

                this.matrix.setTransition(transition);
            }

            // Add symbol behaviours (if configured)
            if (config.symbolBehaviours) {
                for (const symbolBehaviour of config.symbolBehaviours) {
                    for (const symbol of this.matrix.getAllSymbols()) {
                        const behaviour = symbolBehaviour.id;
                        const filteredSymbols = symbolBehaviour.symbols;

                        let behaviourInstance;

                        if (behaviour === "debug") {
                            behaviourInstance = new DebugSymbolBehaviour(symbol);
                        } else if (behaviour === "animate") {
                            behaviourInstance = new AnimateSymbolBehaviour(symbol);
                        } else if (behaviour === "animateOver") {
                            behaviourInstance = new AnimateOverSymbolBehaviour(symbol);
                        } else if (behaviour === "blink") {
                            behaviourInstance = new BlinkSymbolBehaviour(symbol);
                        } else if (behaviour === "landPulse") {
                            behaviourInstance = new LandPulseSymbolBehaviour(symbol);
                        } else if (behaviour === "dim") {
                            behaviourInstance = new DimSymbolBehaviour(symbol);
                        } else if (behaviour === "landAnimateOver") {
                            behaviourInstance = new LandAnimateOverSymbolBehaviour(symbol);
                        } else if (behaviour === "landAnimate") {
                            behaviourInstance = new LandAnimateSymbolBehaviour(symbol);
                        } else if (behaviour === "expandAnimate") {
                            behaviourInstance = new ExpandAnimateSymbolBehaviour(symbol);
                        } else if (behaviour === "anticipateAnimate") {
                            behaviourInstance = new AnticipateAnimateSymbolBehaviour(symbol);
                        } else if (behaviour === "anticipateAnimateOver") {
                            behaviourInstance = new AnticipateAnimateOverSymbolBehaviour(symbol);
                        } else if (behaviour === "stick") {
                            behaviourInstance = new StickSymbolBehaviour(symbol);
                        } else if (behaviour === "stopBounce") {
                            behaviourInstance = new StopBounceSymbolBehaviour(symbol);
                        } else if (behaviour === "idleAnimate") {
                            behaviourInstance = new AnimateIdleSymbolBehavior(symbol);
                        } else if(behaviour === "display") {
                            behaviourInstance = new DisplaySymbolBehaviour(symbol);
                        }

                        if (behaviourInstance) {
                            behaviourInstance.validSymbols = filteredSymbols;
                            symbol.addBehaviour(behaviourInstance);

                            symbol.refresh();
                        }
                    }
                }
            }
        }
    }

    private static createBetBar() {
        this.betBar = new BetBarComponent();
    }

    private static createBigWin() {
        const config = Components.config.bigWin as BigWinConfig;
        const tiers = config.tiers;

        this.bigWin = new BigWinComponent();
        this.bigWin.countupMode = config.linearCountup ? CountMode.LINEAR : CountMode.TIERED;
        this.bigWin.upgrade = config.upgrade;

        if (tiers) {
            for (const tier of tiers) {
                this.bigWin.addWinTier(tier);
            }
        }
    }

    private static createLines() {
        this.lines = new LinesComponent(SlotDefinition.lineDefinitions);
    }

    private static createCelebrateWin() {
        this.celebrateWin = new CelebrateWinComponent();
    }

    private static createPrompts() {
        this.prompt = new PromptComponent();

        if (Components.config.summary) {
            if (Components.config.summary.defaultTime) {
                PromptComponent.DEFAULT_TIME = Components.config.summary.defaultTime;
            }
        }
    }

    private static createPaytable() {
        this.paytable = new PaytableComponent(Layers.PaytableContent, Layers.MenusContent, "paytable");
    }

    private static createBetMenu() {
        let defaultTab: string[] = [];

        if (Components.config.betMenu && Components.config.betMenu.defaultTab) {
            defaultTab = Components.config.betMenu.defaultTab;
        }

        this.betMenu = new BetMenuComponent(defaultTab);
    }

    private static createHeader() {
        this.header = new HeaderComponent();
    }

    private static createFooter() {
        this.footer = new FooterComponent();
    }

    private static createBanner() {
        this.banner = new BannerComponent();

        for (const mode of Object.keys(Components.config.banner)) {
            this.banner.addScenesForMode(mode, Components.config.banner[mode]);
        }

        this.banner.setMode("default");
        this.banner.start();
    }

    public static createPromoComponents() {
        this.promoBetBar = new PromoBetBarComponent();
        this.promoPopups = new Map();
        this.promoPopups.set("intro", new IntroPopup());
        this.promoPopups.set("summary", new SummaryPopup());
        this.promoPopups.set("help", new HelpPopup());
    }
}



// WEBPACK FOOTER //
// ./~/slotworks/components/components.ts